import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetOrganizationsForUserV1UsersUserIdOrganizationsGetData,
  type GetOrganizationsForUserV1UsersUserIdOrganizationsGetError,
  type GetOrganizationsForUserV1UsersUserIdOrganizationsGetResponse,
  getOrganizationsForUserV1UsersUserIdOrganizationsGet,
} from '../../generated';

export type GetOrganizationsForUserQueryVariables = GetOrganizationsForUserV1UsersUserIdOrganizationsGetData['path'] &
  NonNullable<GetOrganizationsForUserV1UsersUserIdOrganizationsGetData['query']>;

export type GetOrganizationsForUserQuery = GetOrganizationsForUserV1UsersUserIdOrganizationsGetResponse;

/**
 * Query hook to fetch organizations accessible by a user, with optional server-side sorting.
 * Cache key includes sort parameters to ensure correct data is displayed when sort changes.
 *
 * @param variables - Path and query parameters including user_id, sort_by, and sort_order
 * @param options - Additional React Query options
 * @returns Query result with organization data
 */
export const useGetOrganizationsForUserQuery = <
  TData = GetOrganizationsForUserQuery,
  TError = GetOrganizationsForUserV1UsersUserIdOrganizationsGetError,
>(
  { user_id, sort_by, sort_order }: GetOrganizationsForUserQueryVariables,
  options?: Omit<UseQueryOptions<GetOrganizationsForUserQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetOrganizationsForUserQuery, TError, TData>({
    queryKey: ['GetOrganizationsForUser', { user_id, sort_by, sort_order }],
    queryFn: async () => {
      const { data } = await getOrganizationsForUserV1UsersUserIdOrganizationsGet({
        path: { user_id },
        query: { sort_by, sort_order },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetOrganizationsForUserQuery.getKey = ({ user_id, sort_by, sort_order }: GetOrganizationsForUserQueryVariables) => [
  'GetOrganizationsForUser',
  { user_id, sort_by, sort_order },
];

useGetOrganizationsForUserQuery.fetcher = (
  { user_id, sort_by, sort_order }: GetOrganizationsForUserQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getOrganizationsForUserV1UsersUserIdOrganizationsGet({
      path: { user_id },
      query: { sort_by, sort_order },
      throwOnError: true,
    });
    return data;
  };
};
